import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StoreSection = () => {
  const navigate = useNavigate();
  const [showcase, setShowcase] = useState(null);
  const [products, setProducts] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  // Entitlement products route to their dedicated paid pages
  const ENTITLEMENT_ROUTES = {
    'U34JWMSAM6CNSAYQD3ZDIAWU': '/request-song',
    'FY264MQD7XKYCPZSWYG4JILI': '/sponsor',
    'H7VCLYKOBFEHJH3UPKOJAVU6': '/pop-your-opp',
    'UST3GEG3GVI66KOMGNAATUZA': '/gu-them',
    'E7LUGQ3W5NOYJXXK6ML2XN2Y': '/one-mic-individual',
    'B7HBMBOXQ5EC6JGJXUHVXBRV': '/one-mic-man-or-woman',
    'AF755U3I7HQTPT43O7KKLFPY': '/come-back-from-the-gu',
    'RDG36QJJ745Z4NPNMD7ZQSFM': '/come-back-from-being-popped',
  };

  useEffect(() => {
    fetch(`${API}/api/store/showcase`).then(r => r.json()).then(d => { if (d) setShowcase(d); }).catch(() => {});
    fetch(`${API}/api/store/products`).then(r => r.json()).then(d => setProducts(d || [])).catch(() => {});
  }, []);

  // Check for checkout completion via URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'complete') {
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleBuy = async (product) => {
    if (!product.square_variation_id || buyingId) return;

    // Entitlement products have dedicated pages with their own checkout flow
    const dedicatedRoute = ENTITLEMENT_ROUTES[product.square_variation_id];
    if (dedicatedRoute) {
      navigate(dedicatedRoute);
      return;
    }

    setBuyingId(product.id);
    setCheckoutError(null);

    try {
      const res = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id: product.square_variation_id,
          quantity: 1,
        }),
      });

      if (!res.ok) {
        throw new Error('Checkout failed');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setCheckoutError(product.id);
      setTimeout(() => setCheckoutError(null), 3000);
    } finally {
      setBuyingId(null);
    }
  };

  if (!showcase && products.length === 0) return null;

  return (
    <section className="px-4 pb-16" data-testid="store-section">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 mb-4">
            <ShoppingBag size={12} className="text-[#E91E8D]" />
            <span className="text-[#E91E8D] text-[10px] font-bold tracking-widest uppercase">Exclusive</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight" data-testid="store-title">
            MEDITERRANEAN CLUB STORE
          </h2>
          <p className="text-white/30 text-xs sm:text-sm tracking-[0.2em] uppercase">
            Premium Apparel &nbsp;&middot;&nbsp; Limited Drops &nbsp;&middot;&nbsp; Exclusive Merchandise
          </p>
        </motion.div>

        {/* Collection Label */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-center mb-8">
          <span className="text-white/15 text-[10px] tracking-[0.3em] uppercase font-bold">Featured Collection</span>
          <h3 className="text-white/60 text-lg sm:text-xl font-light italic mt-1" data-testid="store-collection">
            Respectfully Disrespectful
          </h3>
        </motion.div>

        {/* Showcase Image */}
        {showcase?.image_url && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-16 cursor-pointer" onClick={() => setLightbox(showcase.image_url)} data-testid="store-showcase">
            <div className="rounded-2xl overflow-hidden border border-white/5 relative group">
              <img
                src={showcase.image_url}
                alt="Respectfully Disrespectful Collection"
                className="w-full h-auto"
                style={{ objectFit: 'contain' }}
                data-testid="store-showcase-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          </motion.div>
        )}

        {/* Product Cards */}
        {products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8" data-testid="store-products">
            {products.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <div className="group rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02] hover:border-white/10 transition-all duration-500"
                  data-testid={`product-${p.id}`}>

                  {/* Product Image */}
                  {p.image_url ? (
                    <div className="aspect-[4/5] overflow-hidden bg-black relative cursor-pointer"
                      onClick={() => setLightbox(p.image_url)}>
                      <img src={p.image_url} alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>
                  ) : (
                    <div className="aspect-[4/5] bg-white/[0.02] flex items-center justify-center">
                      <ShoppingBag size={48} className="text-white/5" />
                    </div>
                  )}

                  {/* Product Info */}
                  <div className="p-5 sm:p-6">
                    <h4 className="text-white font-bold text-sm sm:text-base tracking-wide uppercase mb-1">{p.name}</h4>
                    {p.description && (
                      <p className="text-white/30 text-xs leading-relaxed mb-4">{p.description}</p>
                    )}

                    {/* Sizes */}
                    {p.sizes?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {p.sizes.map(s => (
                          <span key={s} className="px-2 py-0.5 rounded border border-white/10 text-white/40 text-[9px] font-semibold uppercase">{s}</span>
                        ))}
                      </div>
                    )}

                    {/* Colors */}
                    {p.colors?.length > 0 && (
                      <div className="flex gap-1.5 mb-4">
                        {p.colors.map(c => (
                          <span key={c} className="px-2 py-0.5 rounded border border-white/10 text-white/30 text-[9px]">{c}</span>
                        ))}
                      </div>
                    )}

                    {/* Price + Button */}
                    <div className="flex items-center justify-between pt-3 border-t border-white/5">
                      <span className="text-white text-lg font-bold tracking-tight">{p.price || 'TBD'}</span>
                      {p.square_variation_id ? (
                        <button
                          className="px-5 py-2 rounded-full bg-white/5 border border-white/10 hover:border-[#E91E8D]/30 hover:bg-[#E91E8D]/5 text-white/50 hover:text-[#E91E8D] text-[10px] font-bold uppercase tracking-widest transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                          data-testid={`buy-${p.id}`}
                          onClick={() => handleBuy(p)}
                          disabled={buyingId === p.id}
                        >
                          {buyingId === p.id ? (
                            <><Loader2 size={12} className="animate-spin" /> Processing</>
                          ) : checkoutError === p.id ? (
                            'Try Again'
                          ) : (
                            'Buy Now'
                          )}
                        </button>
                      ) : (
                        <button className="px-5 py-2 rounded-full bg-white/5 border border-white/10 text-white/30 text-[10px] font-bold uppercase tracking-widest cursor-default"
                          data-testid={`buy-${p.id}`}>
                          Coming Soon
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)} data-testid="store-lightbox">
            <button onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
              <X size={20} className="text-white" />
            </button>
            <motion.img initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              src={lightbox} alt="Enlarged view" className="max-w-full max-h-[90vh]" style={{ objectFit: 'contain' }}
              onClick={e => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default StoreSection;
